/**
 * Scroll reveal: full-viewport main sections (GSAP) + optional .scroll-trigger / .animate--zoom-in.
 * Requires gsap.min.js + ScrollTrigger.min.js (theme.liquid). Body has .animations-reveal-on-scroll when enabled.
 * SP（768px以下）では ScrollTrigger を張らず、スクロールインも発火させない。
 */
(function () {
  const REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');
  /** テーマの SP ブレークポイントに合わせる（max-width: 768px のメディアと一致） */
  const SP = window.matchMedia('(max-width: 768px)');
  const SCROLL_TRIGGER_CLASS = 'scroll-trigger';
  const OFFSCREEN = 'scroll-trigger--offscreen';
  const DESIGN_MODE_CLASS = 'scroll-trigger--design-mode';
  const ZOOM_CLASS = 'animate--zoom-in';
  /** `#MainContent` / フッターグループで JS から付与するスクロールインの見た目（共通） */
  const SECTION_SCROLL_ANIMATE_CLASS = 'animate--fade-in';

  /** `.shopify-section` の id（DOM と同一）。ここに含まれるセクションはスクロールインを付与しない。 */
  const EXCLUDED_SECTION_IDS = new Set([
  ]);

  /** このファイルが生成した ScrollTrigger のみ kill する */
  const animationScrollTriggers = [];

  function hostSection(el) {
    return el.closest('.shopify-section');
  }

  function isExcludedSection(sectionEl) {
    if (!sectionEl || !sectionEl.id) return false;
    if (sectionEl.id.includes('__custom_divider')) return true;
    if (sectionEl.id === 'shopify-section-custom-bottom-bar') return true;
    if (sectionEl.id.includes('__custom_bottom_bar')) return true;
    /* トップのみ: 見出し画像セクションは全画面 min-height / スクロールイン対象外（pin-spacer とも整合） */
    if (
      typeof document !== 'undefined' &&
      document.body.classList.contains('template-index') &&
      sectionEl.id.includes('__custom_heading_image_new')
    ) {
      return true;
    }
    return EXCLUDED_SECTION_IDS.has(sectionEl.id);
  }

  function killAnimationScrollTriggers() {
    animationScrollTriggers.forEach(function (t) {
      if (t && t.kill) t.kill();
    });
    animationScrollTriggers.length = 0;
  }

  /** SP 時でもフェードインを有効化する（常時表示の強制はしない） */

  function applyFullViewportSections() {
    /* トップ専用: 以前の全画面 min-height 付与を無効化（解除のみ）。 */
    if (!document.body || !document.body.classList.contains('template-index')) {
      return;
    }
    /* 旧実装の inline min-height / box-sizing を常に消す */
    function clearViewportMin(section) {
      if (isExcludedSection(section)) return;
      section.style.removeProperty('min-height');
      section.style.removeProperty('box-sizing');
    }
    const main = document.getElementById('MainContent');
    if (main) {
      main.querySelectorAll(':scope > .shopify-section').forEach(clearViewportMin);
    }
    document.querySelectorAll('body.template-index > .shopify-section').forEach(clearViewportMin);

    var footerGroup = document.getElementById('shopify-section-group--footer');
    if (footerGroup) {
      footerGroup.querySelectorAll(':scope .shopify-section').forEach(clearViewportMin);
    }
  }

  /**
   * 未設定の .shopify-section に scroll-trigger + SECTION_SCROLL_ANIMATE_CLASS を付与する（Main / Footer で共通）。
   * @param {HTMLElement | null} container
   * @param {string} sectionSelector 例: ':scope > .shopify-section'（main 直下） / ':scope .shopify-section'（footer グループ内）
   */
  function ensureScrollTriggersForShopifySections(container, sectionSelector) {
    if (!container) return;
    container.querySelectorAll(sectionSelector).forEach(function (section) {
      if (isExcludedSection(section)) return;
      if (section.classList.contains(SCROLL_TRIGGER_CLASS)) return;
      section.classList.add(SCROLL_TRIGGER_CLASS, SECTION_SCROLL_ANIMATE_CLASS);
    });
  }

  function initScrollRevealTriggers() {
    const elements = gsap.utils.toArray(document.getElementsByClassName(SCROLL_TRIGGER_CLASS));
    if (elements.length === 0) return;

    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      elements.forEach(function (el) {
        el.classList.add(DESIGN_MODE_CLASS);
        el.classList.remove(OFFSCREEN);
      });
      return;
    }

    elements.forEach(function (element, index) {
      const sec = hostSection(element);
      if (isExcludedSection(sec)) {
        element.classList.add(DESIGN_MODE_CLASS);
        element.classList.remove(OFFSCREEN);
        return;
      }
      element.classList.add(OFFSCREEN);
      var st = ScrollTrigger.create({
        trigger: element,
        start: 'top 90%',
        once: true,
        onEnter: function () {
          element.classList.remove(OFFSCREEN);
          if (element.hasAttribute('data-cascade')) {
            element.style.setProperty('--animation-order', String(index));
          }
        },
      });
      animationScrollTriggers.push(st);
    });
  }

  function initZoomIn() {
    if (SP.matches) return;
    if (REDUCE.matches) return;
    const scaleAmount = 0.2 / 100;
    gsap.utils.toArray(document.getElementsByClassName(ZOOM_CLASS)).forEach(function (el) {
      const host = hostSection(el);
      if (host && isExcludedSection(host)) return;
      var st = ScrollTrigger.create({
        trigger: el,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true,
        onUpdate: function (self) {
          var p = Math.round(self.progress * 100);
          el.style.setProperty('--zoom-in-ratio', String(1 + scaleAmount * p));
        },
      });
      animationScrollTriggers.push(st);
    });
  }

  function applyScrollAnimationLayer() {
    if (typeof ScrollTrigger === 'undefined' || typeof gsap === 'undefined') {
      return;
    }
    if (!document.body || !document.body.classList.contains('animations-reveal-on-scroll')) {
      return;
    }
    gsap.registerPlugin(ScrollTrigger);
    killAnimationScrollTriggers();

    const mainEl = document.getElementById('MainContent');
    ensureScrollTriggersForShopifySections(mainEl, ':scope > .shopify-section');

    /* TOP only: sections after custom_image_banner_xUYfct use slide-up + fade (animate--slide-in). */
    if (mainEl && document.body.classList.contains('template-index')) {
      const sections = Array.from(mainEl.querySelectorAll(':scope > .shopify-section'));
      const bannerIdx = sections.findIndex(function (s) {
        return s.id && s.id.indexOf('__custom_image_banner_xUYfct') !== -1;
      });
      if (bannerIdx !== -1) {
        sections.slice(bannerIdx + 1).forEach(function (s) {
          if (!s.classList.contains('scroll-trigger')) return;
          // 項目/カード単位のstaggerで見せるセクションは、セクション単位の表示アニメ(slide-in/fade-in)を外す
          // （二重掛け防止・セクションは即表示）。PICK PRODUCTS / TOPICS が該当。
          if (
            s.id &&
            (s.id.indexOf('__custom_collection_swatches') !== -1 ||
              s.id.indexOf('__custom_topics') !== -1)
          ) {
            s.classList.remove('animate--fade-in');
            s.classList.remove('animate--slide-in');
            return;
          }
          s.classList.remove('animate--fade-in');
          s.classList.add('animate--slide-in');
          /* もう少し下から(5rem)・ぬるっと。文字アニメ(1.4s / power2.out相当)と揃える。 */
          s.style.setProperty(
            '--animation-slide-in',
            'slideInUp 1.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards'
          );
        });
      }
    }

    ensureScrollTriggersForShopifySections(
      document.getElementById('shopify-section-group--footer'),
      ':scope .shopify-section'
    );
    initScrollRevealTriggers();
    initZoomIn();

    ScrollTrigger.refresh();
  }

  function boot() {
    if (!document.body) {
      return;
    }

    /* トップのみ: セクション min-height（全画面レイアウト） */
    applyFullViewportSections();

    if (typeof ScrollTrigger === 'undefined' || typeof gsap === 'undefined') {
      return;
    }

    /* テーマ設定「スクロールで表示」がオンの全テンプレ: Main / Footer のスクロールイン */
    applyScrollAnimationLayer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  REDUCE.addEventListener('change', function () {
    if (typeof ScrollTrigger === 'undefined') return;
    applyScrollAnimationLayer();
  });

  SP.addEventListener('change', function () {
    applyFullViewportSections();
    if (typeof ScrollTrigger === 'undefined' || typeof gsap === 'undefined') return;
    applyScrollAnimationLayer();
  });

  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      gsap.utils.toArray(event.target.getElementsByClassName(SCROLL_TRIGGER_CLASS)).forEach(function (el) {
        el.classList.add(DESIGN_MODE_CLASS);
        el.classList.remove(OFFSCREEN);
      });
      if (typeof ScrollTrigger !== 'undefined') ScrollTrigger.refresh();
    });
    document.addEventListener('shopify:section:reorder', function () {
      if (typeof ScrollTrigger !== 'undefined') ScrollTrigger.refresh();
    });
  }
})();
